import bytes from 'bytes';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';
import { FileWithPreview } from './types';
import ImagePreview from './ImagePreview';
import PdfPreview from './PdfPreview';
import VideoPreview from './VideoPreview';
import MiniImagePreview from './MiniImagePreview';

interface Props {
  file: File;
  onFileChange: (file: File) => void;
  subLabel?: string | React.ReactNode;
  maxSize?: number;
  acceptFileTypes?: Accept;
  error?: string;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  containerClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
  previewItemClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
  previewItemWidth?: string; // CSS width
  previewItemHeight?: string; // CSS height
  isMini?: boolean;
  hideErrorMessage?: 'internal' | 'external' | 'both';
}

export default function PreviewDropzoneWithPaste({
  file,
  onFileChange,
  subLabel,
  maxSize = 5 * 1024 * 1024,
  acceptFileTypes,
  error,
  className,
  containerClassName,
  previewItemClassName,
  previewItemWidth,
  previewItemHeight,
  isMini,
  hideErrorMessage,
}: Props) {
  const intl = useIntl();
  const [preview, setPreview] = useState<string>(undefined);
  const [pasteError, setPasteError] = useState<string>(undefined);
  const [isHoveringDropzone, setIsHoveringDropzone] = useState(false);

  const isAcceptedByType = useCallback(
    (incomingFile: File) => {
      if (!acceptFileTypes || Object.keys(acceptFileTypes).length === 0) return true;

      const fileName = incomingFile.name.toLowerCase();
      const mimeType = incomingFile.type;

      return Object.entries(acceptFileTypes).some(([acceptedMime, extensions]) => {
        const isWildcardMime = acceptedMime === '*/*';
        const isCategoryMime = acceptedMime.endsWith('/*');
        const mimeMatches = isWildcardMime
          ? true
          : isCategoryMime
            ? mimeType.startsWith(acceptedMime.replace('*', ''))
            : mimeType === acceptedMime;

        const normalizedExtensions = (extensions || []).map((ext) => ext.toLowerCase());
        const extensionMatches = normalizedExtensions.some((ext) => fileName.endsWith(ext));

        return mimeMatches || extensionMatches;
      });
    },
    [acceptFileTypes]
  );

  const removePreview = () => {
    URL.revokeObjectURL(preview);
    setPreview(undefined);
  };

  const removeFile = () => {
    removePreview();
    setPasteError(undefined);
    onFileChange(null);
  };

  useEffect(() => {
    if (file && !preview) {
      setPreview(URL.createObjectURL(file));
    }

    // Clean up object URL when component unmounts
    return () => {
      if (file && preview) {
        removePreview();
      }
    };
  }, [file]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;

      if (preview) URL.revokeObjectURL(preview);
      setPasteError(undefined);
      onFileChange(acceptedFiles[0]);
      setPreview(URL.createObjectURL(acceptedFiles[0]));
    },
    [file, onFileChange]
  );

  const handlePastedFile = useCallback(
    (pastedFile: File) => {
      if (!pastedFile) return;

      if (pastedFile.size > maxSize) {
        setPasteError(`File is larger than ${bytes(maxSize)}`);
        return;
      }

      if (!isAcceptedByType(pastedFile)) {
        setPasteError('File type is not accepted');
        return;
      }

      onDrop([pastedFile]);
    },
    [isAcceptedByType, maxSize, onDrop]
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const clipboardFiles = Array.from(event.clipboardData?.files || []);
      const pastedFile = clipboardFiles[0];

      if (!pastedFile) return;

      event.preventDefault();
      event.stopPropagation();

      handlePastedFile(pastedFile);
    },
    [handlePastedFile]
  );

  useEffect(() => {
    if (!isHoveringDropzone) return;

    const onWindowPaste = (event: ClipboardEvent) => {
      const clipboardFiles = Array.from(event.clipboardData?.files || []);
      const pastedFile = clipboardFiles[0];

      if (!pastedFile) return;

      event.preventDefault();
      handlePastedFile(pastedFile);
    };

    window.addEventListener('paste', onWindowPaste);

    return () => {
      window.removeEventListener('paste', onWindowPaste);
    };
  }, [handlePastedFile, isHoveringDropzone]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxSize,
    maxFiles: 1,
    multiple: false,
    accept: acceptFileTypes,
  });

  // Combine internal and external errors
  const hasInternalErrors = fileRejections.length > 0 || !!pasteError;
  const showExternalError = !!error && !hasInternalErrors;

  return (
    <div className={clsx('flex flex-col gap-2', containerClassName)}>
      <div
        {...getRootProps({
          onPaste,
          tabIndex: 0,
          onMouseEnter: () => setIsHoveringDropzone(true),
          onMouseLeave: () => setIsHoveringDropzone(false),
        })}
        className={clsx(
          'd-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded-3',
          isMini ? 'p-2' : 'p-7',
          isDragActive && 'active bg-light-primary',
          hasInternalErrors || showExternalError
            ? 'border-danger'
            : isDragActive
              ? 'border-primary'
              : 'border-gray-300',
          className
        )}
      >
        <input {...getInputProps()} />
        {file ? (
          <>
            {file.type.startsWith('image/') &&
              (isMini ? (
                <MiniImagePreview
                  file={{ ...file, preview }}
                  onRemove={() => removeFile()}
                  containerClassName={previewItemClassName}
                  width={previewItemWidth}
                  height={previewItemHeight}
                />
              ) : (
                <ImagePreview
                  file={{ ...file, preview }}
                  onRemove={() => removeFile()}
                  className={previewItemClassName}
                />
              ))}
            {file.type.startsWith('video') && (
              <VideoPreview
                file={{ ...file, preview }}
                onRemove={() => removeFile()}
                className={previewItemClassName}
              />
            )}
            {file.type.endsWith('pdf') && (
              <PdfPreview
                file={{ ...file, preview }}
                onRemove={() => removeFile()}
                className={previewItemClassName}
              />
            )}
          </>
        ) : (
          <>
            <p className='text-center m-0'>
              {isDragActive
                ? intl.formatMessage({ id: 'FILE_DROPZONE.DRAG_AND_DROP_FILE' })
                : intl.formatMessage({ id: 'FILE_DROPZONE.DROP_OR_SELECT_FILE' })}
            </p>
            {subLabel ?? (
              <>
                {acceptFileTypes && (
                  <div className='form-text'>
                    {intl.formatMessage(
                      { id: 'FILE_DROPZONE.ACCEPTED_FILE_TYPES' },
                      {
                        fileTypes: (
                          <span className='fw-semibold'>
                            {Object.values(acceptFileTypes)
                              .flatMap((type) => type)
                              .join(', ')}
                          </span>
                        ),
                      }
                    )}
                  </div>
                )}
                <div className='form-text'>
                  {intl.formatMessage(
                    { id: 'FILE_DROPZONE.MAX_FILE_SIZE' },
                    {
                      maxSize: <span className='fw-semibold'>{bytes(maxSize)}</span>,
                    }
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {!['internal', 'both'].includes(hideErrorMessage) && hasInternalErrors && (
        <div className='text-danger text-sm'>
          {fileRejections.map(({ file, errors }) => (
            <p key={file.name}>
              {file.name} — {errors.map((err) => err.message).join('; ')}
            </p>
          ))}
          {pasteError && <p>{pasteError}</p>}
        </div>
      )}

      {!['external', 'both'].includes(hideErrorMessage) && showExternalError && (
        <span role='alert' className='text-danger text-xs'>
          {error}
        </span>
      )}
    </div>
  );
}
